/**
 * Traduce los errores conocidos de PocketBase al español para que el constructor pueda
 * entenderlos y corregirlos. Los mensajes desconocidos se conservan para no ocultar
 * información útil.
 *
 * Los códigos técnicos no se traducen y pueden acompañar al detalle del error. Estas
 * traducciones solo afectan al texto mostrado; no se usan para decidir el comportamiento.
 */

/** Para buscar sin tropezar con la mayúscula inicial ni el punto final. */
const clave = (text: string): string =>
  text.trim().toLowerCase().replace(/\s+/g, " ").replace(/\.+$/, "");

/**
 * Las frases que llevan un número dentro: el límite viaja a la traducción.
 *
 * Van antes que todo lo demás porque el código solo no lo dice --dos columnas
 * con el mismo código se quejan de largos distintos-- y perder el número sería
 * dejar el aviso sin lo único que hay que corregir.
 */
const CON_DATO: [RegExp, string][] = [
  [/^the length must be between (\d+) and (\d+)$/, "Debe tener entre $1 y $2 caracteres"],
  [/^the length must be no less than (\d+)$/, "Debe tener al menos $1 caracteres"],
  [/^the length must be no more than (\d+)$/, "No puede superar $1 caracteres"],
  [/^the length must be exactly (\d+)$/, "Debe tener exactamente $1 caracteres"],
  [/^must be at least (\d+) character\(s\)$/, "Debe tener al menos $1 caracteres"],
  [/^must be no more than (\d+) character\(s\)$/, "No puede superar $1 caracteres"],
  [/^must be no less than (-?[\d.]+)$/, "No puede ser menor que $1"],
  [/^must be no greater than (-?[\d.]+)$/, "No puede ser mayor que $1"],
  [/^must be larger than (-?[\d.]+)$/, "Tiene que ser mayor que $1"],
  [/^must be less than or equal to (-?[\d.]+)$/, "No puede ser mayor que $1"],
];

/**
 * Por el nombre técnico del fallo, que es lo que no cambia.
 *
 * PocketBase reescribe sus frases entre versiones; el código no. Cuando viene
 * --y en lo que se objeta a una columna viene casi siempre-- manda el.
 */
const POR_CODIGO: Record<string, string> = {
  validation_required: "Este campo es obligatorio. No puede quedar vacío",
  validation_not_unique: "Ya hay otra fila con este valor y la columna no admite repetidos",
  validation_invalid_value: "El valor no es válido. Revisa el tipo de dato",
  validation_invalid_format: "El formato no es válido. Revisa el dato",
  validation_not_a_number: "Escribe un número válido",
  validation_date_invalid: "Escribe una fecha válida",
  validation_date_out_of_range: "La fecha está fuera del intervalo permitido",
  validation_is_email: "Escribe un correo electrónico válido",
  validation_invalid_email: "Escribe un correo electrónico válido",
  validation_invalid_new_email: "Escribe un correo electrónico válido",
  validation_email_domain_not_allowed: "Ese dominio de correo no se admite",
  validation_is_url: "Escribe una dirección web válida",
  validation_invalid_json: "El contenido no es un JSON válido",
  validation_in_invalid: "Elige una de las opciones de la columna",
  validation_missing_rel_records: "No se encontró la fila con la que enlazar",
  validation_invalid_relation: "La relación apunta a una fila que no existe",
  validation_too_many_files: "Son más archivos de los que admite la columna",
  validation_invalid_file: "El archivo no se pudo leer",
  validation_invalid_mime_type: "Ese tipo de archivo no se admite en la columna",
  validation_file_size_limit: "El archivo supera el tamaño permitido",
  validation_values_mismatch: "Los dos valores no coinciden",
  validation_invalid_password: "La contraseña no es correcta",
  validation_invalid_old_password: "La contraseña actual no es correcta",
  validation_invalid_token: "El enlace o el código no es válido o ha caducado",
  validation_invalid_record: "No se puede guardar la fila. Revisa sus datos",
  validation_not_found: "No se encontró",
  validation_duplicated_field_name: "Ya hay otra columna con ese nombre",
  validation_reserved_field_name: "Ese nombre de columna está reservado",
  validation_collection_name_exists: "Ya hay otra tabla con ese nombre",
};

/**
 * Por la frase, para lo que llega sin código.
 *
 * Es el caso de los niveles de fuera de un lote --"Batch transaction failed",
 * "Failed to update record"--, que son mensajes de la API y no quejas contra
 * una columna. Las claves van como las deja `clave()`: en minúsculas y sin el
 * punto del final.
 */
const POR_FRASE: Record<string, string> = {
  "batch transaction failed": "No se guardó ningún cambio del grupo de filas",
  "failed to create record": "No se pudo crear la fila",
  "failed to update record": "No se pudo guardar la fila",
  "failed to delete record": "No se pudo eliminar la fila",
  "failed to delete record. make sure that the record is not part of a required relation reference":
    "No se pudo eliminar la fila: otra fila necesita este vínculo",
  "failed to authenticate": "No se pudo iniciar sesión. Revisa el correo y la contraseña",
  "failed to load the submitted data due to invalid formatting":
    "No se pudieron leer los datos enviados. Revisa el formato",
  "an error occurred while validating the submitted data":
    "Hay datos incorrectos. Revísalos antes de continuar",
  "something went wrong while processing your request":
    "No se pudo completar la acción. Inténtalo de nuevo",
  "you are not allowed to perform this request": "No tienes permiso para realizar esta acción",
  "the request requires valid record authorization token":
    "Tu sesión ha caducado. Vuelve a iniciar sesión",
  "the requested resource wasn't found": "No se encontró el contenido solicitado",
  "missing required record id": "Selecciona la fila para continuar",
  "too many requests": "Se enviaron demasiadas solicitudes. Espera un momento e inténtalo de nuevo",
  // Lo que se ve al cambiar una tabla; ver `recreateChanged` en `server/schema.ts`.
  "failed to create collection": "No se pudo crear la tabla",
  "failed to update collection": "No se pudo cambiar la tabla",
  "failed to delete collection": "No se pudo eliminar la tabla",
  "field type cannot be changed": "No se puede cambiar el tipo de una columna existente",
  "the relation collection cannot be changed":
    "No se puede cambiar la tabla de destino de una relación existente",
  // Las mismas quejas de arriba, por si alguna llega sin su código.
  "value must be unique": "Ya hay otra fila con este valor y la columna no admite repetidos",
  "cannot be blank": "Este campo es obligatorio. No puede quedar vacío",
  "missing required value": "Este campo es obligatorio. No puede quedar vacío",
  "invalid value": "El valor no es válido. Revisa el tipo de dato",
  "must be a valid value": "El valor no es válido. Revisa el tipo de dato",
  "must be a valid email address": "Escribe un correo electrónico válido",
  "must be a valid url": "Escribe una dirección web válida",
  "must be a valid date": "Escribe una fecha válida",
  "must be a number": "Escribe un número válido",
  "must be in a valid format": "El formato no es válido. Revisa el dato",
};

/**
 * La frase de PocketBase en español, o la suya propia si no está traducida.
 *
 * `code` es el nombre técnico que acompaña a lo que se le objeta a una columna,
 * cuando lo hay.
 */
export function enEspanol(message: string, code?: string): string {
  const text = message.trim();
  if (!text) return text;

  const key = clave(text);
  for (const [patron, dicho] of CON_DATO) {
    const found = key.match(patron);
    if (found) return dicho.replace(/\$(\d)/g, (_, n: string) => found[Number(n)] ?? "");
  }

  return (code ? POR_CODIGO[code] : undefined) ?? POR_FRASE[key] ?? text;
}
